import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProfileImageModal from "../../molecules/ProfileImageModal/ProfileImageModal";
import { uploadFile } from "../../../Redux/Apis/employeeApi";
import { changePasswordAPI, ensureUserProfile, updateUserApi } from "../../../Redux/Apis/authApi";
import { setAuthLoading } from "../../../Redux/Slices/authSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import { DEFAULT_PROFILE_IMAGE } from "../../../app/utils/common/baseUrl";
import "./ProfilePage.scss";

const formatDateOnly = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().slice(0, 10);
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.auth.auth);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [pwd, setPwd] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPwd, setShowPwd] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const handleUpdateProfileImage = async (file) => {
    const formData = new FormData();
    formData.append("folderName", "profile");
    formData.append("files", file);
    dispatch(setAuthLoading(true));
    try {
      const body = await dispatch(uploadFile(formData)).unwrap();
      const url = body?.data?.[0];
      if (url) {
        await dispatch(updateUserApi({ profileImage: url })).unwrap();
      }
      await ensureUserProfile(dispatch, { force: true });
    } catch (_e) {
      // Errors toast via reducers
    } finally {
      dispatch(setAuthLoading(false));
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = pwd;
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      showErrorToast("All password fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      showErrorToast("New password and confirmation must match.");
      return;
    }
    setPwdSubmitting(true);
    try {
      await dispatch(
        changePasswordAPI({ currentPassword, newPassword, confirmPassword })
      ).unwrap();
      showSuccessToast("Password updated successfully.");
      setPwd({ currentPassword: "", newPassword: "", confirmPassword: "" });
      await ensureUserProfile(dispatch, { force: true });
    } catch (_err) {
      // Toast from auth reducer
    } finally {
      setPwdSubmitting(false);
    }
  };

  const displayPhone =
    user?.countryCode && user?.phoneNumber
      ? `${user.countryCode} ${user.phoneNumber}`
      : user?.phoneNumber || "-";

  const renderEyeIcon = (isVisible) =>
    isVisible ? (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 3l18 18" strokeLinecap="round" />
        <path d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" strokeLinecap="round" />
        <path
          d="M9.88 5.08A10.94 10.94 0 0112 5c5.25 0 9.27 3.11 10.5 7-1.03 3.25-3.96 5.92-7.85 6.74M6.61 6.61C4.81 7.83 3.45 9.62 2.5 12a11.8 11.8 0 002.45 3.87"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path
          d="M2.5 12C3.73 8.11 7.75 5 13 5s9.27 3.11 10.5 7c-1.23 3.89-5.25 7-10.5 7S3.73 15.89 2.5 12z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="13" cy="12" r="3" />
      </svg>
    );

  const renderPasswordInput = (key, label, autoComplete) => (
    <div>
      <label className="block text-sm font-medium text-[#5f6d8a] mb-1">{label}</label>
      <div className="relative">
        <input
          type={showPwd[key] ? "text" : "password"}
          autoComplete={autoComplete}
          className="search-input w-full !text-black pr-10"
          value={pwd[key]}
          onChange={(e) => setPwd((s) => ({ ...s, [key]: e.target.value }))}
        />
        <button
          type="button"
          onClick={() => setShowPwd((s) => ({ ...s, [key]: !s[key] }))}
          className="absolute inset-y-0 right-3 text-[#5f6d8a] hover:text-[#27314a] focus:outline-none"
          aria-label={showPwd[key] ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
        >
          {renderEyeIcon(showPwd[key])}
        </button>
      </div>
    </div>
  );

  return (
    <div className="profile-page card-border card-body">
      <h1 className="page-title">Profile</h1>
      <p className="page-subtitle">
        Update your profile photo and password. Other details are shown for reference; contact HR to change name or email
        if required. Use a strong new password (8–32 characters with upper and lower case, a number, and a symbol).
      </p>

      <div className="profile-page__grid mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="profile-page__section p-6 rounded-xl border border-[#e6ebf6] bg-[#f9fbff]">
          <h2 className="profile-page__section-title text-lg font-semibold text-[#27314a] mb-4">Profile picture</h2>
          <div className="profile-page__profile-row flex flex-col sm:flex-row gap-6 items-start">
            <button
              type="button"
              onClick={() => setPhotoModalOpen(true)}
              className="profile-page__avatar-btn relative rounded-full overflow-hidden border-2 border-[#e6ebf6] w-28 h-28 shrink-0 focus:outline-none focus:ring-2 focus:ring-[#744dff]"
            >
              <img
                src={user?.profileImage || DEFAULT_PROFILE_IMAGE}
                alt=""
                className="w-full h-full object-cover"
              />
              <span className="absolute inset-x-0 bottom-0 py-1 text-xs text-white bg-black/50">
                Change
              </span>
            </button>
            <div className="profile-page__details text-sm text-[#6f7d97] space-y-1">
              <p>
                <span className="font-medium text-[#27314a]">Name:</span>{" "}
                {user?.firstname} {user?.lastname}
              </p>
              <p>
                <span className="font-medium text-[#27314a]">Email:</span> {user?.email || "-"}
              </p>
              <p>
                <span className="font-medium text-[#27314a]">Phone:</span> {displayPhone}
              </p>
              <p>
                <span className="font-medium text-[#27314a]">Date of birth:</span>{" "}
                {formatDateOnly(user?.dob)}
              </p>
            </div>
          </div>
        </section>

        <section className="profile-page__section p-6 rounded-xl border border-[#e6ebf6] bg-[#f9fbff]">
          <h2 className="profile-page__section-title text-lg font-semibold text-[#27314a] mb-2">Change password</h2>
          <p className="profile-page__section-desc text-sm text-[#6f7d97] mb-4 max-w-md">
            Enter your current password, then choose a new one. The server requires at least 8 characters including
            uppercase, lowercase, a digit, and a special character.
          </p>
          <form onSubmit={submitPassword} className="profile-page__form space-y-4 max-w-md">
            {renderPasswordInput("currentPassword", "Current password", "current-password")}
            {renderPasswordInput("newPassword", "New password", "new-password")}
            {renderPasswordInput("confirmPassword", "Confirm new password", "new-password")}
            <button
              type="submit"
              disabled={pwdSubmitting || loading}
              className="profile-page__submit button bg-btn-primary text-white px-6 py-2 rounded-lg disabled:opacity-60"
            >
              {pwdSubmitting ? "Updating…" : "Update password"}
            </button>
          </form>
        </section>
      </div>

      <ProfileImageModal
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        currentImage={user?.profileImage}
        onUpdateImage={handleUpdateProfileImage}
      />
    </div>
  );
};

export default ProfilePage;
